// Validation utility functions for forms

export const validateUrl = (url) => {
  if (!url || url.trim() === "") return { isValid: true, error: "" };

  try {
    const urlObj = new URL(url);
    if (!["http:", "https:"].includes(urlObj.protocol)) {
      return { isValid: false, error: "URL must use HTTP or HTTPS protocol" };
    }
    return { isValid: true, error: "" };
  } catch (error) {
    return {
      isValid: false,
      error: "Please enter a valid URL (e.g., https://example.com)",
    };
  }
};

export const validateRequiredText = (
  value,
  fieldName,
  minLength = 1,
  maxLength = 255,
) => {
  if (
    !value ||
    (typeof value === "object" && !value.en) ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const textValue = typeof value === "object" ? value.en : value;
  const trimmedValue = textValue.trim();

  if (trimmedValue.length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} character${minLength > 1 ? "s" : ""}`,
    };
  }

  if (trimmedValue.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must be no more than ${maxLength} characters`,
    };
  }

  return { isValid: true, error: "" };
};

export const validateNumericRange = (
  value,
  fieldName,
  min,
  max,
  required = false,
) => {
  if (!required && (value === "" || value === undefined || value === null)) {
    return { isValid: true, error: "" };
  }

  if (required && (value === "" || value === undefined || value === null)) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const numValue = Number(value);

  if (isNaN(numValue) || !isFinite(numValue)) {
    return { isValid: false, error: `${fieldName} must be a valid number` };
  }

  if (min !== undefined && numValue < min) {
    return { isValid: false, error: `${fieldName} must be at least ${min}` };
  }

  if (max !== undefined && numValue > max) {
    return {
      isValid: false,
      error: `${fieldName} must be no more than ${max}`,
    };
  }

  return { isValid: true, error: "" };
};

export const validateMinMaxRange = (
  minValue,
  maxValue,
  minFieldName = "Minimum",
  maxFieldName = "Maximum",
) => {
  const errors = { min: "", max: "" };

  const minNum = Number(minValue);
  const maxNum = Number(maxValue);

  // Individual validation
  if (
    minValue !== "" &&
    minValue !== undefined &&
    (isNaN(minNum) || !isFinite(minNum))
  ) {
    errors.min = `${minFieldName} must be a valid number`;
  }

  if (
    maxValue !== "" &&
    maxValue !== undefined &&
    (isNaN(maxNum) || !isFinite(maxNum))
  ) {
    errors.max = `${maxFieldName} must be a valid number`;
  }

  // Cross validation
  if (
    minValue !== "" &&
    maxValue !== "" &&
    minValue !== undefined &&
    maxValue !== undefined
  ) {
    if (minNum >= maxNum) {
      errors.min = `${minFieldName} must be less than ${maxFieldName}`;
      errors.max = `${maxFieldName} must be greater than ${minFieldName}`;
    }
  }

  return errors;
};

export const validateHexColor = (color) => {
  if (!color || color.trim() === "") {
    return { isValid: false, error: "Color is required" };
  }

  const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  if (!hexPattern.test(color)) {
    return {
      isValid: false,
      error: "Color must be a valid hex color (e.g., #FF0000 or #F00)",
    };
  }

  return { isValid: true, error: "" };
};

export const validateColorPalette = (palette) => {
  if (!Array.isArray(palette) || palette.length < 2) {
    return {
      isValid: false,
      error: "Color palette must have at least 2 colors",
    };
  }

  if (palette.length > 20) {
    return {
      isValid: false,
      error: "Color palette cannot have more than 20 colors",
    };
  }

  // Check for duplicate colors
  const uniqueColors = new Set(palette.map((color) => color.toLowerCase()));
  if (uniqueColors.size !== palette.length) {
    return {
      isValid: false,
      error: "Color palette cannot contain duplicate colors",
    };
  }

  // Validate each color
  for (let i = 0; i < palette.length; i++) {
    const colorValidation = validateHexColor(palette[i]);
    if (!colorValidation.isValid) {
      return {
        isValid: false,
        error: `Color ${i + 1}: ${colorValidation.error}`,
      };
    }
  }

  return { isValid: true, error: "" };
};

export const validateFileUpload = (file, allowedTypes = [], maxSizeMB = 10) => {
  if (!file) {
    return { isValid: true, error: "" };
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `File size must be less than ${maxSizeMB}MB`,
    };
  }

  // Check file type
  if (allowedTypes.length > 0) {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      return {
        isValid: false,
        error: `File must be one of: ${allowedTypes.join(", ")}`,
      };
    }
  }

  return { isValid: true, error: "" };
};

export const validateArray = (arr, fieldName, minItems = 0, maxItems = 100) => {
  if (!Array.isArray(arr)) {
    return { isValid: false, error: `${fieldName} must be an array` };
  }

  if (arr.length < minItems) {
    return {
      isValid: false,
      error: `${fieldName} must have at least ${minItems} item${minItems > 1 ? "s" : ""}`,
    };
  }

  if (arr.length > maxItems) {
    return {
      isValid: false,
      error: `${fieldName} cannot have more than ${maxItems} items`,
    };
  }

  return { isValid: true, error: "" };
};

export const validateEmailDomain = (email, allowedDomains = []) => {
  if (!email || email.trim() === "") {
    return { isValid: false, error: "Email is required" };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: "Please enter a valid email address" };
  }

  if (allowedDomains.length === 0) {
    return { isValid: true, error: "" };
  }

  const emailDomain = email.split("@")[1].toLowerCase();
  const isAllowed = allowedDomains.some(
    (domain) =>
      emailDomain === domain.toLowerCase() ||
      emailDomain.endsWith("." + domain.toLowerCase()),
  );

  if (!isAllowed) {
    const domainList = allowedDomains.join(", ");
    return {
      isValid: false,
      error: `Access is restricted to email addresses from: ${domainList}`,
    };
  }

  return { isValid: true, error: "" };
};
